import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { withStyles } from "@mui/styles";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { forEach } from "lodash";

import { performRequest } from "../../../network/perform-request";
import { participantsShowRoute, userProfileGet } from "../../../network/api-routes";
import sports from "constants/sports";
import RevaText from "components/common/RevaText";
import UserContext from "context/UserContext";

const styles = (theme) => ({
  modalContainer: {
    width: "100%",
    padding: theme.spacing(4),
    margin: "auto",
    borderRadius: 5,
  },
  sectionTitle: {
    fontWeight: 600,
  },
  userCard: {
    padding: theme.spacing(2),
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  metaRow: {
    color: theme.palette.text.secondary,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  tableHeadCell: {
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  highlightCell: {
    fontWeight: 600,
  },
});

const parseRows = (statsBySport) => {
  const rows = [];
  forEach(statsBySport, (category) => {
    forEach(category, (stat) => {
      const sportData = sports.find((s) => s.sport_id === parseInt(stat.sport_id, 10));
      if (!sportData) return;
      rows.push({
        id: `${sportData.sport_id}-${stat.category.id}`,
        sportName: sportData.name,
        category: stat.category.name,
        played: stat.played_tournaments,
        highestPosition: stat.highest_position_label,
        highestPositionCount: stat.highest_position_count,
        tournament: stat.tournament?.competition_name,
        champion: stat.highest_position === 1,
      });
    });
  });
  return rows;
};

const getPlayerUserIds = (players = []) => {
  const userIds = players
    .map((player) => (player.playerable_type === "App\\User" ? player.playerable_id : player.playerable?.user_id))
    .filter(Boolean);

  return [...new Set(userIds)];
};

const SportsStatsTable = ({ rows, classes }) => {
  const intl = useIntl();

  if (!rows || rows.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <RevaText variant="body2" className={classes.metaRow} id="Este jugador aún no tiene historial de torneos." />
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>{intl.formatMessage({ id: "Deporte" })}</TableCell>
            <TableCell className={classes.tableHeadCell}>{intl.formatMessage({ id: "Categoría" })}</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>
              {intl.formatMessage({ id: "Jugados" })}
            </TableCell>
            <TableCell className={classes.tableHeadCell}>{intl.formatMessage({ id: "Posición más alta" })}</TableCell>
            <TableCell align="center" className={classes.tableHeadCell}>
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <span>{intl.formatMessage({ id: "Veces" })}</span>
                <Tooltip
                  title={intl.formatMessage({
                    id: "Cantidad de torneos en los que alcanzó esa posición más alta",
                  })}
                >
                  <InfoOutlinedIcon fontSize="inherit" />
                </Tooltip>
              </Stack>
            </TableCell>
            <TableCell className={classes.tableHeadCell}>{intl.formatMessage({ id: "En el torneo" })}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <SportsTennisIcon fontSize="small" color="action" />
                  <span>{row.sportName}</span>
                </Stack>
              </TableCell>
              <TableCell>{row.category}</TableCell>
              <TableCell align="center">{row.played}</TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {row.champion && <EmojiEventsIcon fontSize="small" color="warning" />}
                  <RevaText className={classes.highlightCell} id={row.highestPosition} />
                </Stack>
              </TableCell>
              <TableCell align="center">
                {row.highestPositionCount != null ? <Chip size="small" label={row.highestPositionCount} /> : "—"}
              </TableCell>
              <TableCell>{row.tournament || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const UserProfileCard = ({ profile, classes }) => {
  const { user, stats_by_sport } = profile;
  const userData = user.data || user;
  const rows = parseRows(stats_by_sport);

  return (
    <Paper variant="outlined" className={classes.userCard}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Avatar src={userData.full_profile_pic_url || user.full_profile_pic_url} sx={{ width: 56, height: 56 }} />
        <Stack>
          <RevaText variant="h3" sx={{ fontWeight: 600 }}>
            {userData.full_name}
          </RevaText>
          {userData.username && (
            <RevaText variant="caption" className={classes.metaRow}>
              {`@${userData.username}`}
            </RevaText>
          )}
        </Stack>
      </Stack>
      <Divider sx={{ mb: 1 }} />
      <SportsStatsTable rows={rows} classes={classes} />
    </Paper>
  );
};

const ProfilesModal = ({ participant, editParticipant, classes }) => {
  const { user: authUser } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [ready, setReady] = useState(false);
  const [usersProfiles, setUsersProfiles] = useState([]);
  const [showEmpty, setShowEmpty] = useState(false);

  const fetch = useCallback(() => {
    const loadProfiles = (players = []) => {
      const userIds = getPlayerUserIds(players);

      if (userIds.length === 0) {
        setShowEmpty(true);
        setReady(true);
        return;
      }

      Promise.all(
        userIds.map((userId) =>
          performRequest("POST", userProfileGet, { user_id: userId }, true, authUser)
            .then((res) => res.data)
            .catch(() => null),
        ),
      ).then((profiles) => {
        const validProfiles = profiles.filter(Boolean);
        setUsersProfiles(validProfiles);
        setShowEmpty(validProfiles.length === 0);
        setReady(true);
      });
    };

    setReady(false);
    setShowEmpty(false);
    setUsersProfiles([]);

    if (participant?.team?.players?.length > 0) {
      loadProfiles(participant.team.players);
      return;
    }

    if (!participant?.id) {
      setShowEmpty(true);
      setReady(true);
      return;
    }

    performRequest("POST", participantsShowRoute, { participant_id: participant.id }, true, authUser)
      .then((response) => {
        loadProfiles(response.data.data?.team?.players || []);
      })
      .catch((error) => {
        setShowEmpty(true);
        setReady(true);
        enqueueSnackbar(error?.response?.data?.message || "Error", { variant: "error" });
      });
  }, [participant, authUser, enqueueSnackbar]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (!ready) {
    return (
      <Grid container className={classes.modalContainer} justifyContent="center">
        <CircularProgress size={24} />
      </Grid>
    );
  }

  if (showEmpty) {
    return (
      <Box className={classes.modalContainer}>
        <RevaText variant="h4" sx={{ fontWeight: 600, mb: 1 }} id="Historial de torneos" />
        <Box className={classes.emptyState}>
          <EmojiEventsIcon sx={{ fontSize: 48, mb: 1 }} color="disabled" />
          <RevaText variant="h5" id="No hay datos de estos jugadores." />
          <RevaText variant="body2" sx={{ mt: 1 }} id="Vincula una cuenta Reva a cada jugador para ver su historial." />
          {editParticipant && (
            <RevaText
              variant="body2"
              sx={{ mt: 1, color: "primary.main", cursor: "pointer" }}
              onClick={() => editParticipant()}
              id="Volver a gestionar participante"
            />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.modalContainer}>
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <RevaText variant="h4" className={classes.sectionTitle} id="Historial de torneos" />
        <RevaText
          variant="caption"
          className={classes.metaRow}
          id="Estadísticas por deporte y categoría de cada jugador vinculado a una cuenta Reva."
        />
      </Stack>
      {usersProfiles.map((profile) => (
        <UserProfileCard key={profile.user?.data?.id || profile.user?.id} profile={profile} classes={classes} />
      ))}
    </Box>
  );
};

export default withStyles(styles)(ProfilesModal);
